import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TextInput,
  Modal,
  ActivityIndicator,
  ScrollView,
  Image,
} from 'react-native';
import { LoadingSpinner } from '../common/LoadingSpinner';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { useAuth } from '../../context/AuthContext';
import { storeAPI } from '../../services/api';
import { useTheme, ThemeColors } from '../../context/ThemeContext';
import { Toast } from '../common/Toast';
import { Logger } from '../../utils/logger';
import {
  CartLine as StoreCartLine,
  addToCart as addLineToCart,
  cartTotal as computeCartTotal,
  changeQuantity as stepQuantity,
  hoursLeft,
  quantityInCart,
} from '../../utils/storeCart';

/**
 * The school store, shared by the web and native Store screens.
 *
 * Parents browse the inventory catalog, pick sizes where a product has them,
 * and place an order. Ordering RESERVES stock for 48 hours — the banner and
 * the confirmation both say so — and payment happens at the school office,
 * where the order sits in the collection terminal's Pre-orders queue.
 */

interface StoreProduct {
  id: string;
  name: string;
  description?: string | null;
  categoryName?: string | null;
  unitName?: string | null;
  sellingPrice: number;
  taxPercent: number;
  availableQuantity: number;
  sizes: string[];
  imageIds: string[];
}

type CartLine = StoreCartLine & { product: StoreProduct };

interface MyOrder {
  id: string;
  orderNumber: string;
  status: string;
  totalAmount: number;
  createdDate: string;
  reservedUntil: string;
  items: { id: string; productName: string; size?: string | null; quantity: number; lineTotal: number }[];
}

const STATUS_META: Record<string, { label: string; fg: string; bg: string }> = {
  PENDING_PAYMENT: { label: 'Awaiting payment', fg: '#92400E', bg: '#FEF3C7' },
  PAID: { label: 'Ready for pickup', fg: '#065F46', bg: '#D1FAE5' },
  COLLECTED: { label: 'Collected', fg: '#374151', bg: '#F3F4F6' },
  CANCELLED: { label: 'Cancelled', fg: '#6B7280', bg: '#F3F4F6' },
  EXPIRED: { label: 'Expired', fg: '#991B1B', bg: '#FEE2E2' },
};

const imageCache = new Map<string, string>();

function ProductPhoto({ productId, imageId, style }: {
  productId: string;
  imageId?: string;
  style: any;
}) {
  const key = imageId ? `${productId}/${imageId}` : '';
  const [uri, setUri] = useState<string | null>(key ? imageCache.get(key) ?? null : null);

  useEffect(() => {
    let cancelled = false;
    if (!imageId || uri) return;
    (async () => {
      try {
        const { data } = await storeAPI.getProductImageBlob(productId, imageId);
        const reader = new FileReader();
        reader.onloadend = () => {
          if (!cancelled && typeof reader.result === 'string') {
            imageCache.set(key, reader.result);
            setUri(reader.result);
          }
        };
        reader.readAsDataURL(data as Blob);
      } catch {
        /* missing photo renders as the placeholder */
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, imageId]);

  if (!uri) {
    return (
      <View style={[style, { alignItems: 'center', justifyContent: 'center', backgroundColor: '#EEF2FF' }]}>
        <FeatherIcon name="image" size={22} color="#A5B4FC" />
      </View>
    );
  }
  return <Image source={{ uri }} style={style} resizeMode="cover" />;
}


export function StoreContent() {
  const { students, selectedStudent } = useAuth();
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);

  const [products, setProducts] = useState<StoreProduct[]>([]);
  const [orders, setOrders] = useState<MyOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState<string>('ALL');
  const [cart, setCart] = useState<CartLine[]>([]);
  const [notes, setNotes] = useState('');
  const [placing, setPlacing] = useState(false);
  const [sizePickerFor, setSizePickerFor] = useState<StoreProduct | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setError(null);
      const [productsRes, ordersRes] = await Promise.all([
        storeAPI.getProducts(),
        storeAPI.getMyOrders(),
      ]);
      setProducts(productsRes.data ?? []);
      setOrders(ordersRes.data?.data ?? []);
    } catch (err) {
      Logger.error('Store', 'Failed to load the store', err);
      setError('Could not load the store. Pull to refresh or try again later.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const categories = useMemo(() => {
    const names = Array.from(new Set(products.map(p => p.categoryName).filter(Boolean))) as string[];
    return ['ALL', ...names];
  }, [products]);

  const visibleProducts = useMemo(
    () => products.filter(p => category === 'ALL' || p.categoryName === category),
    [products, category]);

  const cartQuantityFor = (productId: string) => quantityInCart(cart, productId);

  const addToCart = (product: StoreProduct, size?: string) =>
    setCart(prev => addLineToCart(prev, product, size) as CartLine[]);

  const handleAdd = (product: StoreProduct) => {
    if (product.sizes.length > 0) setSizePickerFor(product);
    else addToCart(product);
  };

  const changeQuantity = (line: CartLine, delta: number) =>
    setCart(prev => stepQuantity(prev, line, delta) as CartLine[]);

  const cartTotal = computeCartTotal(cart);

  const placeOrder = async () => {
    if (cart.length === 0 || placing) return;
    setPlacing(true);
    try {
      const res = await storeAPI.placeOrder({
        items: cart.map(l => ({ productId: l.product.id, quantity: l.quantity, size: l.size ?? null })),
        studentProfileId: selectedStudent?.id ?? students[0]?.id ?? null,
        notes: notes.trim() || null,
      });
      setCart([]);
      setNotes('');
      setToastMessage(res.data?.message
        ?? 'Order placed. Items are reserved for 48 hours — pay at the school office to collect.');
      setShowToast(true);
      load();
    } catch (err: any) {
      Logger.error('Store', 'Failed to place order', err);
      setToastMessage(err?.response?.data?.message || 'Could not place the order. Please try again.');
      setShowToast(true);
    } finally {
      setPlacing(false);
    }
  };

  const cancelOrder = async (order: MyOrder) => {
    try {
      await storeAPI.cancelOrder(order.id);
      setToastMessage(`Order ${order.orderNumber} cancelled. The items are back in stock.`);
      setShowToast(true);
      load();
    } catch (err: any) {
      setToastMessage(err?.response?.data?.message || 'Could not cancel this order.');
      setShowToast(true);
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading the store..." />;
  }

  return (
    <ScrollView showsVerticalScrollIndicator={false}>
      {/* The reservation rule, stated up front where nobody can miss it. */}
      <View style={styles.holdBanner}>
        <FeatherIcon name="clock" size={16} color="#92400E" style={{ marginTop: 2 }} />
        <Text style={styles.holdBannerText}>
          Ordering reserves your items for 48 hours. Pay at the school office within
          that time — unpaid orders expire and the items go back to stock.
        </Text>
      </View>

      {!!error && (
        <View style={styles.errorBanner}>
          <FeatherIcon name="alert-circle" size={16} color={colors.danger} />
          <Text style={styles.errorBannerText}>{error}</Text>
          <TouchableOpacity onPress={load}><Text style={styles.retryText}>Retry</Text></TouchableOpacity>
        </View>
      )}

      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.chipRow}>
        {categories.map(name => (
          <TouchableOpacity
            key={name}
            style={[styles.chip, category === name && styles.chipActive]}
            onPress={() => setCategory(name)}
          >
            <Text style={[styles.chipText, category === name && styles.chipTextActive]}>
              {name === 'ALL' ? 'All items' : name}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>

      {/* ── The shelves ── */}
      <View style={styles.grid}>
        {visibleProducts.map(product => {
          const inCart = cartQuantityFor(product.id);
          const soldOut = product.availableQuantity <= 0;
          return (
            <View key={product.id} style={[styles.productCard, soldOut && styles.productCardOut]}>
              <ProductPhoto
                productId={product.id}
                imageId={product.imageIds[0]}
                style={styles.productImage}
              />
              <Text style={styles.productName} numberOfLines={1}>{product.name}</Text>
              <Text style={styles.productMeta}>
                ${product.sellingPrice.toFixed(2)}
                {product.categoryName ? ` · ${product.categoryName}` : ''}
              </Text>
              {product.sizes.length > 0 && (
                <Text style={styles.productSizes} numberOfLines={1}>
                  Sizes: {product.sizes.join(', ')}
                </Text>
              )}
              {soldOut ? (
                <View style={styles.outPill}><Text style={styles.outPillText}>Out of stock</Text></View>
              ) : (
                <TouchableOpacity style={styles.addBtn} onPress={() => handleAdd(product)}>
                  <FeatherIcon name="plus" size={13} color="#FFF" />
                  <Text style={styles.addBtnText}>Add{inCart > 0 ? ` (${inCart})` : ''}</Text>
                </TouchableOpacity>
              )}
            </View>
          );
        })}
      </View>

      {/* ── The cart ── */}
      {cart.length > 0 && (
        <View style={styles.cartBox}>
          <Text style={styles.sectionTitle}>Your order</Text>
          {cart.map((line, i) => (
            <View key={`${line.product.id}-${line.size ?? ''}-${i}`} style={styles.cartRow}>
              <View style={{ flex: 1 }}>
                <Text style={styles.cartName}>
                  {line.product.name}{line.size ? ` (${line.size})` : ''}
                </Text>
                <Text style={styles.cartPrice}>${line.product.sellingPrice.toFixed(2)} each</Text>
              </View>
              <View style={styles.qtyControls}>
                <TouchableOpacity style={styles.qtyBtn} onPress={() => changeQuantity(line, -1)}>
                  <FeatherIcon name="minus" size={13} color={colors.textSecondary} />
                </TouchableOpacity>
                <Text style={styles.qtyText}>{line.quantity}</Text>
                <TouchableOpacity style={styles.qtyBtn} onPress={() => changeQuantity(line, +1)}>
                  <FeatherIcon name="plus" size={13} color={colors.textSecondary} />
                </TouchableOpacity>
              </View>
            </View>
          ))}

          <TextInput
            style={styles.notesInput}
            placeholder="Note for the office (optional)"
            placeholderTextColor={colors.textMuted}
            value={notes}
            onChangeText={setNotes}
            maxLength={500}
          />

          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>Total (incl. tax)</Text>
            <Text style={styles.totalValue}>${cartTotal.toFixed(2)}</Text>
          </View>

          <TouchableOpacity
            style={[styles.placeBtn, placing && { opacity: 0.6 }]}
            onPress={placeOrder}
            disabled={placing}
          >
            {placing
              ? <ActivityIndicator size="small" color="#FFF" />
              : <Text style={styles.placeBtnText}>Reserve for 48 h · pay at school</Text>}
          </TouchableOpacity>
        </View>
      )}

      {/* ── My orders ── */}
      <Text style={[styles.sectionTitle, { marginTop: 20 }]}>My orders</Text>
      {orders.length === 0 ? (
        <Text style={styles.emptyOrders}>Nothing ordered yet.</Text>
      ) : (
        orders.map(order => {
          const meta = STATUS_META[order.status] ?? STATUS_META.PENDING_PAYMENT;
          const pending = order.status === 'PENDING_PAYMENT';
          return (
            <View key={order.id} style={styles.orderCard}>
              <View style={styles.orderHeader}>
                <Text style={styles.orderNumber}>{order.orderNumber} · ${order.totalAmount.toFixed(2)}</Text>
                <View style={[styles.statusPill, { backgroundColor: meta.bg }]}>
                  <Text style={[styles.statusPillText, { color: meta.fg }]}>{meta.label}</Text>
                </View>
              </View>
              <Text style={styles.orderItems} numberOfLines={2}>
                {order.items.map(i =>
                  `${i.productName}${i.size ? ` (${i.size})` : ''} × ${i.quantity}`).join(' · ')}
              </Text>
              {pending && (
                <View style={styles.orderFooter}>
                  <Text style={styles.holdLeftText}>
                    <FeatherIcon name="clock" size={11} /> Held for another {hoursLeft(order.reservedUntil)} h — pay at the school office
                  </Text>
                  <TouchableOpacity onPress={() => cancelOrder(order)} style={styles.cancelBtn} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                    <Text style={styles.cancelText}>Cancel</Text>
                  </TouchableOpacity>
                </View>
              )}
              {order.status === 'PAID' && (
                <Text style={styles.readyText}>Paid — collect from the school office.</Text>
              )}
              {order.status === 'EXPIRED' && (
                <Text style={styles.expiredText}>
                  The 48-hour hold lapsed and the items went back to stock. Order again any time.
                </Text>
              )}
            </View>
          );
        })
      )}

      {/* ── Size picker ── */}
      <Modal visible={!!sizePickerFor} transparent animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={styles.modal}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Pick a size — {sizePickerFor?.name}</Text>
              <TouchableOpacity onPress={() => setSizePickerFor(null)}>
                <FeatherIcon name="x" size={22} color={colors.textSecondary} />
              </TouchableOpacity>
            </View>
            <View style={styles.sizeWrap}>
              {(sizePickerFor?.sizes ?? []).map(size => (
                <TouchableOpacity
                  key={size}
                  style={styles.sizeChip}
                  onPress={() => {
                    if (sizePickerFor) addToCart(sizePickerFor, size);
                    setSizePickerFor(null);
                  }}
                >
                  <Text style={styles.sizeChipText}>{size}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        </View>
      </Modal>

      <Toast
        visible={showToast}
        message={toastMessage}
        type="success"
        onDismiss={() => setShowToast(false)}
      />
      <View style={{ height: 32 }} />
    </ScrollView>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  holdBanner: { flexDirection: 'row', gap: 10, backgroundColor: '#FEF3C7', borderRadius: 12, padding: 12, marginBottom: 14 },
  holdBannerText: { flex: 1, fontSize: 12.5, color: '#92400E', lineHeight: 18, fontWeight: '600' },
  errorBanner: { flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: colors.dangerLight, borderRadius: 10, padding: 12, marginBottom: 12 },
  errorBannerText: { flex: 1, fontSize: 12, color: colors.textPrimary },
  retryText: { fontSize: 12, fontWeight: '700', color: colors.primary },
  chipRow: { gap: 8, paddingBottom: 14 },
  chip: { paddingHorizontal: 14, paddingVertical: 7, borderRadius: 999, backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border },
  chipActive: { backgroundColor: colors.primary, borderColor: colors.primary },
  chipText: { fontSize: 12, fontWeight: '600', color: colors.textSecondary },
  chipTextActive: { color: '#FFF' },
  grid: { flexDirection: 'row', flexWrap: 'wrap', gap: 10 },
  productCard: { width: 160, backgroundColor: colors.card, borderRadius: 12, borderWidth: 1, borderColor: colors.divider, padding: 10 },
  productCardOut: { opacity: 0.55 },
  productImage: { width: '100%', height: 90, borderRadius: 8, marginBottom: 8 },
  productName: { fontSize: 13.5, fontWeight: '700', color: colors.textPrimary },
  productMeta: { fontSize: 12, color: colors.textSecondary, marginTop: 2 },
  productSizes: { fontSize: 11, color: colors.textMuted, marginTop: 2 },
  addBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 5, backgroundColor: colors.primary, borderRadius: 8, paddingVertical: 7, marginTop: 8 },
  addBtnText: { color: '#FFF', fontSize: 12, fontWeight: '700' },
  outPill: { backgroundColor: colors.dangerLight, borderRadius: 8, paddingVertical: 7, marginTop: 8, alignItems: 'center' },
  outPillText: { color: colors.danger, fontSize: 12, fontWeight: '700' },
  cartBox: { backgroundColor: colors.card, borderRadius: 12, borderWidth: 1, borderColor: colors.divider, padding: 14, marginTop: 18 },
  sectionTitle: { fontSize: 15, fontWeight: '800', color: colors.textPrimary, marginBottom: 10 },
  cartRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 6 },
  cartName: { fontSize: 13, fontWeight: '600', color: colors.textPrimary },
  cartPrice: { fontSize: 11.5, color: colors.textSecondary },
  qtyControls: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  qtyBtn: { width: 26, height: 26, borderRadius: 13, borderWidth: 1, borderColor: colors.border, alignItems: 'center', justifyContent: 'center' },
  qtyText: { fontSize: 13, fontWeight: '700', color: colors.textPrimary, minWidth: 18, textAlign: 'center' },
  notesInput: { borderWidth: 1, borderColor: colors.border, borderRadius: 8, padding: 10, fontSize: 13, color: colors.textPrimary, marginTop: 8 },
  totalRow: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 12 },
  totalLabel: { fontSize: 13, color: colors.textSecondary },
  totalValue: { fontSize: 16, fontWeight: '800', color: colors.textPrimary },
  placeBtn: { backgroundColor: colors.primary, borderRadius: 10, paddingVertical: 12, alignItems: 'center', marginTop: 12 },
  placeBtnText: { color: '#FFF', fontSize: 14, fontWeight: '700' },
  emptyOrders: { fontSize: 13, color: colors.textMuted, marginBottom: 8 },
  orderCard: { backgroundColor: colors.card, borderRadius: 12, borderWidth: 1, borderColor: colors.divider, padding: 12, marginBottom: 10 },
  // The order number gives way (the amount wraps under it on a narrow phone) so the status pill
  // always shows in full - it used to be pushed off the card (bug 17424).
  orderHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 },
  orderNumber: { flexShrink: 1, fontSize: 13, lineHeight: 18, fontWeight: '700', color: colors.textPrimary },
  statusPill: { flexShrink: 0, borderRadius: 999, paddingHorizontal: 10, paddingVertical: 3 },
  statusPillText: { fontSize: 10.5, fontWeight: '800' },
  orderItems: { fontSize: 12, color: colors.textSecondary, marginTop: 6 },
  orderFooter: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginTop: 8 },
  holdLeftText: { flex: 1, fontSize: 11.5, lineHeight: 16, color: '#92400E', fontWeight: '600' },
  cancelBtn: { flexShrink: 0 },
  cancelText: { fontSize: 12, fontWeight: '700', color: colors.danger },
  readyText: { fontSize: 12, color: '#065F46', fontWeight: '600', marginTop: 8 },
  expiredText: { fontSize: 11.5, color: colors.textMuted, marginTop: 8, lineHeight: 16 },
  modalOverlay: { flex: 1, backgroundColor: colors.overlay, justifyContent: 'center', alignItems: 'center', padding: 20 },
  modal: { backgroundColor: colors.card, borderRadius: 16, padding: 18, width: '100%', maxWidth: 420 },
  modalHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 },
  modalTitle: { fontSize: 15, fontWeight: '800', color: colors.textPrimary, flex: 1, marginRight: 8 },
  sizeWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: 10 },
  sizeChip: { borderWidth: 1, borderColor: colors.primary, borderRadius: 10, paddingHorizontal: 18, paddingVertical: 10 },
  sizeChipText: { fontSize: 14, fontWeight: '700', color: colors.primary },
});
